/**
 * parseSearchQuery.js
 * 
 * Parses natural language search queries like:
 *   "hotels in bamenda"         → { location: "bamenda", keywords: ["hotels"] }
 *   "rent house in yaoundé"     → { location: "yaoundé", keywords: ["rent", "house"] }
 *   "hotels near douala"        → { location: "douala", keywords: ["hotels"] }
 *   "appartement à buea"        → { location: "buea", keywords: ["appartement"] }
 *   "plombier près de douala"   → { location: "douala", keywords: ["plombier"] }
 *   "clean villa limbe"         → { location: "limbe", keywords: ["clean", "villa"] }
 */

// Preposition patterns that signal the next word(s) are a location
const LOCATION_PREPOSITIONS_EN = ['in', 'near', 'at', 'around', 'within', 'nearby', 'close to'];
const LOCATION_PREPOSITIONS_FR = ['à', 'a', 'près de', 'pres de', 'autour de', 'dans', 'vers', 'chez'];

// Compound prepositions (multi-word) — match from longest first
const COMPOUND_PREPOSITIONS = ['près de', 'pres de', 'autour de', 'close to'];

// Common property/service type keywords to ignore when extracting location
const TYPE_KEYWORDS_EN = [
  'hotel', 'hotels', 'motel', 'motels',
  'house', 'houses', 'home', 'homes', 'apartment', 'apartments', 'flat', 'flats',
  'villa', 'villas', 'studio', 'studios', 'room', 'rooms',
  'rent', 'renting', 'rented', 'sale', 'sell', 'buy', 'buying', 'bought',
  'property', 'properties', 'real estate',
  'clean', 'cleaning', 'cleaner', 'plumber', 'plumbing', 'electrician',
  'catering', 'caterer', 'photography', 'photographer',
  'transport', 'transportation', 'taxi', 'driver',
  'salon', 'barber', 'barbershop',
  'garden', 'gardening', 'landscaping',
  'security', 'guard', 'guarding',
  'cooking', 'cook', 'chef',
  'laundry', 'dry cleaning',
  'moving', 'mover', 'relocation',
  'painting', 'painter',
  'repair', 'repairing', 'renovation', 'renovating',
  'baby', 'babysitting', 'nanny',
  'tutor', 'tutoring', 'teaching', 'teacher',
  'massage', 'spa',
  'gym', 'fitness',
  'party', 'events', 'event',
  'hair', 'hairdresser', 'nail', 'nails',
  'real', 'estate',
];
const TYPE_KEYWORDS_FR = [
  'hôtel', 'hotels', 'hôtel', 'motels',
  'maison', 'maisons', 'appartement', 'appartements', 'appart',
  'villa', 'villas', 'studio', 'studios', 'chambre', 'chambres',
  'location', 'louer', 'loué', 'vente', 'vendre', 'acheter',
  'propriété', 'propriétés', 'immobilier',
  'nettoyage', 'nettoyer', 'plombier', 'plomberie', 'électricien',
  'cuisine', 'cuisinier', 'traiteur',
  'photo', 'photographe', 'photographie',
  'transport', 'taxi', 'chauffeur',
  'salon', 'coiffeur', 'barbier',
  'jardin', 'jardinage', 'paysagisme',
  'sécurité', 'gardien',
  'lessive', 'pressing',
  'déménagement', 'déménageur',
  'peinture', 'peintre',
  'réparation', 'rénovation', 'rénover',
  'baby-sitting', 'nounou',
  'tutorat', 'tuteur', 'cours', 'professeur',
  'massage', 'spa',
  'salle', 'sport',
  'fête', 'événement', 'événements',
  'cheveux', 'manucure', 'ongles',
  'propreté', 'propre',
];

// Cameroon cities for better detection
const CAMEROON_CITIES = [
  'douala', 'yaoundé', 'yaounde', 'bafoussam', 'bamenda', 'buea', 'buee',
  'garoua', 'maroua', 'ngaoundéré', 'ngaoundere', 'kumba', 'limbe',
  'bertoua', 'ebolowa', 'kribi', 'limbé', 'baffoussam', 'foumban',
  'mbouda', 'nchang', 'tiko', 'rick', 'menoua', 'wouri',
  'sam', 'menda', 'makurdi',
];

/**
 * Parse a search query string into location and remaining keywords.
 * @param {string} query - The raw search input
 * @returns {{ location: string, keywords: string[], rawQuery: string }}
 */
export function parseSearchQuery(query) {
  if (!query || typeof query !== 'string') {
    return { location: '', keywords: [], rawQuery: '' };
  }

  const rawQuery = query.trim();
  const lower = rawQuery.toLowerCase();

  // 1. Try compound prepositions first ("près de", "close to", etc.)
  for (const cp of COMPOUND_PREPOSITIONS) {
    const idx = lower.indexOf(cp);
    if (idx !== -1) {
      const after = rawQuery.substring(idx + cp.length).trim();
      const location = after.trim();
      const before = rawQuery.substring(0, idx).trim();
      const keywords = extractKeywords(before);
      if (location) {
        return { location, keywords, rawQuery };
      }
    }
  }

  // 2. Try single-word prepositions
  const allPreps = [...LOCATION_PREPOSITIONS_EN, ...LOCATION_PREPOSITIONS_FR];
  for (const prep of allPreps) {
    // Match "in bamenda", "in Yaoundé", etc. — the prep must be a whole word
    const regex = new RegExp(`\\b${escapeRegex(prep)}\\s+(.+)$`, 'i');
    const match = rawQuery.match(regex);
    if (match) {
      const location = match[1].trim();
      const before = rawQuery.substring(0, rawQuery.toLowerCase().indexOf(prep.toLowerCase())).trim();
      const keywords = extractKeywords(before);
      if (location) {
        return { location, keywords, rawQuery };
      }
    }
  }

  // 3. Try to detect if the last word is a known city
  const words = lower.split(/\s+/);
  if (words.length >= 2) {
    const lastWord = words[words.length - 1];
    const lastTwoWords = words.slice(-2).join(' ');
    // Check two-word city names first
    if (CAMEROON_CITIES.includes(lastTwoWords)) {
      const location = rawQuery.substring(rawQuery.toLowerCase().lastIndexOf(lastTwoWords)).trim();
      const before = rawQuery.substring(0, rawQuery.toLowerCase().lastIndexOf(lastTwoWords)).trim();
      const keywords = extractKeywords(before);
      return { location, keywords, rawQuery };
    }
    // Check single-word city
    if (CAMEROON_CITIES.includes(lastWord)) {
      const location = lastWord;
      const before = words.slice(0, -1).join(' ');
      const keywords = extractKeywords(before);
      return { location, keywords, rawQuery };
    }
  }

  // 4. No location detected — treat the entire query as keywords
  return { location: '', keywords: extractKeywords(rawQuery), rawQuery };
}

/**
 * Extract meaningful keywords from a string, filtering out common type words
 * and prepositions that aren't useful for text matching.
 */
function extractKeywords(text) {
  if (!text) return [];
  const stopWords = new Set([
    ...LOCATION_PREPOSITIONS_EN,
    ...LOCATION_PREPOSITIONS_FR,
    'the', 'a', 'an', 'and', 'or', 'but', 'for', 'to', 'of', 'with', 'is',
    'le', 'la', 'les', 'un', 'une', 'des', 'et', 'ou', 'pour', 'de', 'du', 'au',
    'sur', 'que', 'qui', 'est', 'son', 'sa', 'ses',
  ]);
  const typeWords = new Set([...TYPE_KEYWORDS_EN, ...TYPE_KEYWORDS_FR]);

  return text
    .toLowerCase()
    .split(/\s+/)
    .map(w => w.replace(/[^\wàâäéèêëïîôùûüÿçœæ'-]/g, ''))
    .filter(w => w.length > 1)
    .filter(w => !stopWords.has(w))
    .filter(w => !typeWords.has(w));
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Match a property/hotel/service against parsed query results.
 * Returns true if the item matches both location and keywords.
 * 
 * @param {object} item - The data item to match
 * @param {{ location: string, keywords: string[] }} parsed - Parsed query
 * @param {object} fieldMap - Map of field accessors { name, location, description, category }
 * @returns {boolean}
 */
export function matchesParsedQuery(item, parsed, fieldMap) {
  if (!parsed.location && parsed.keywords.length === 0) return true;

  const itemName = (fieldMap.name?.(item) || '').toLowerCase();
  const itemLocation = (fieldMap.location?.(item) || '').toLowerCase();
  const itemDescription = (fieldMap.description?.(item) || '').toLowerCase();
  const itemCategory = (fieldMap.category?.(item) || '').toLowerCase();

  // Check location match
  if (parsed.location) {
    const loc = parsed.location.toLowerCase();
    const locationMatch = itemLocation.includes(loc);
    // Also check if the name or description mentions the location
    const nameOrDescMatch = itemName.includes(loc) || itemDescription.includes(loc);
    if (!locationMatch && !nameOrDescMatch) {
      return false;
    }
  }

  // Check keyword matches (at least one keyword must match somewhere)
  if (parsed.keywords.length > 0) {
    const searchableText = `${itemName} ${itemLocation} ${itemDescription} ${itemCategory}`;
    const hasKeywordMatch = parsed.keywords.some(kw => searchableText.includes(kw));
    if (!hasKeywordMatch) {
      return false;
    }
  }

  return true;
}

export default parseSearchQuery;
